import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import {
  useTheme,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import WarningIcon from "@icons/myicons/warning.svg?react";
import { toCurrency } from "@helpers";
import { GainLossTypography } from "@components/common";
import { MandatorTypography } from "@components/MandatorTypography";

const ValuesContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: end;

  ${({ theme }) => theme.breakpoints.down("lg")} {
    flex-direction: column;
    justify-content: normal;
  }
`;

const NotificationContainer = styled.div<{ showNotification: boolean }>`
  align-items: center;
  justify-content: flex-end;
  color: ${({ theme }) => theme.palette.text.secondary};
  display: ${({ showNotification }) => (showNotification ? "flex" : "none")};
`;

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  flex-grow: 1;
  height: 100%;
`;

export interface AccountRowSummaryProps {
  amount: number;
  currency: string;
  sinceYear?: number;
  changeAbs: number;
  showNotification?: boolean;
  onClickUpdate?: (event: React.MouseEvent<HTMLElement>) => void;
  isUpdating?: boolean;
}

export const AccountRowSummary = ({
  amount,
  currency,
  sinceYear,
  changeAbs,
  showNotification = false,
  onClickUpdate,
  isUpdating = false,
}: AccountRowSummaryProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <SummaryWrapper>
      <ValuesContainer>
        <MandatorTypography variant={isSm ? "body2" : "subtitle2"}>
          {toCurrency(amount, currency)}
        </MandatorTypography>

        {Boolean(sinceYear && changeAbs && !isSm) && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
          >
            <Typography
              variant="body4"
              color={theme.palette.text.secondary}
              noWrap
            >
              {t("depotPage.since")} {sinceYear}:
            </Typography>
            <GainLossTypography variant="body4" value={changeAbs}>
              {toCurrency(changeAbs, currency)}
            </GainLossTypography>
          </Box>
        )}

        <NotificationContainer
          showNotification={showNotification}
          onClick={isUpdating ? undefined : onClickUpdate}
        >
          <Typography
            variant="caption"
            ml={1}
            color={theme.palette.text.warning}
          >
            <Box
              component="span"
              sx={{
                display: "inline-block",
                mr: "4px",
                lineHeight: 0,
                verticalAlign: "bottom",
              }}
            >
              {isUpdating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <WarningIcon
                  width="20px"
                  height="20px"
                  style={{ minWidth: "20px", minHeight: "20px" }}
                  color={theme.palette.icon.warning}
                />
              )}
            </Box>
            {!isMd && t("domains.accountPreviewCard.updateRecommended")}
          </Typography>
        </NotificationContainer>
      </ValuesContainer>
    </SummaryWrapper>
  );
};
